// src/pages/ELearning/TrashTests.tsx
import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { Trash2, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';

interface DeletedTest {
  _id: string;
  title: string;
  category: string;
  deletedAt: string;
}

const TrashTests: React.FC = () => {
  const [tests, setTests] = useState<DeletedTest[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const confirm = useConfirm();

  const fetchDeletedTests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/elearning/tests/deleted');
      setTests(res.data);
    } catch (err) {
      showToast('Не удалось загрузить корзину', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedTests();
  }, []);

  const handleRestore = async (id: string) => {
    try {
      await api.post(`/elearning/tests/${id}/restore`);
      showToast('Тест восстановлен', 'success');
      fetchDeletedTests();
    } catch (err) {
      showToast('Не удалось восстановить', 'error');
    }
  };

  const handleDeletePermanently = (id: string) => {
    confirm.showConfirm({
      title: 'Окончательное удаление',
      message: 'Тест будет удалён безвозвратно. Продолжить?',
      confirmText: 'Удалить',
      onConfirm: async () => {
        try {
          await api.delete(`/elearning/tests/${id}/permanent`);
          showToast('Тест удалён', 'success');
          fetchDeletedTests();
        } catch (err) {
          showToast('Не удалось удалить', 'error');
        }
      },
    });
  };

  const handleClearTrash = () => {
    confirm.showConfirm({
      title: 'Очистка корзины',
      message: 'Все тесты в корзине будут удалены безвозвратно. Продолжить?',
      confirmText: 'Очистить',
      onConfirm: async () => {
        try {
          await api.post('/elearning/tests/trash/clear');
          showToast('Корзина очищена', 'success');
          fetchDeletedTests();
        } catch (err) {
          showToast('Не удалось очистить корзину', 'error');
        }
      },
    });
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  return (
    <>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Корзина тестов</h1>
          {tests.length > 0 && (
            <button onClick={handleClearTrash} style={styles.clearButton}>
              <AlertTriangle size={16} /> Очистить корзину
            </button>
          )}
        </div>
        {tests.length === 0 ? (
          <p style={styles.empty}>Корзина пуста</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Категория</th>
                <th>Удалён</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {tests.map(test => (
                <tr key={test._id}>
                  <td>{test.title}</td>
                  <td>{test.category}</td>
                  <td>{new Date(test.deletedAt).toLocaleDateString()}</td>
                  <td>
                    <button onClick={() => handleRestore(test._id)} style={styles.actionButton}>
                      <RefreshCw size={14} /> Восстановить
                    </button>
                    <button onClick={() => handleDeletePermanently(test._id)} style={{ ...styles.actionButton, background: '#ff4d4f' }}>
                      <Trash2 size={14} /> Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 1000, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  clearButton: { background: '#ff4d4f', color: 'white', border: 'none', borderRadius: 30, padding: '10px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 },
  empty: { textAlign: 'center', color: '#999', padding: 40 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'rgba(255,255,255,0.3)', backdropFilter: 'blur(8px)', borderRadius: 16, overflow: 'hidden' },
  actionButton: { background: '#52c41a', color: 'white', border: 'none', borderRadius: 20, padding: '6px 12px', cursor: 'pointer', marginRight: 8, display: 'inline-flex', alignItems: 'center', gap: 4 },
};

export default TrashTests;